import ElementStore from 'src/stores/alt/stores/ElementStore';
import UIStore from 'src/stores/alt/stores/UIStore';
import UserStore from 'src/stores/alt/stores/UserStore';
import ElementActions from 'src/stores/alt/actions/ElementActions';
import Sample from 'src/models/Sample';
import { presence } from 'src/chemkit';
import {
  getRememberedReviewState,
  normalizeReviewState,
  rememberReviewState,
} from 'src/addon/reviewStateMemory';
import { parseReviewStateChangedPayload } from 'src/addon/reviewLockProjection';
import {
  EVENT_ADDON_WAKE,
  SOURCE_UI_TABS_REFRESH,
  WINDOW_KEY_AUTOSAVE_CONFIG,
  WINDOW_KEY_AUTOSAVE_RUNTIME,
} from './config';
import { ensureDashboardConfig } from './dashboardConfig';
import { toPaneKey, parseUpdatedAtMs, toPositiveInt } from './utils';
import { createEntityIndex } from './entityIndex';
import { createTabsProjection } from './tabsProjection';
import { createLockProjection } from './lockProjection';
import { createReviewHandlers } from './reviewHandlers';
import { createSaveExecutor } from './saveExecutor';
import { createNotificationAcknowledgement } from './notificationAcknowledgement';
import { composeBridge } from './composeBridge';
import { createChemotionHostAdapter } from './hostAdapter';

export default function installChemkitHostBridge() {
  ensureDashboardConfig(window);

  const hostAdapter = createChemotionHostAdapter({
    windowRef: window,
    ElementStore,
    ElementActions,
    UIStore,
    UserStore,
    Sample,
    autosaveConfigWindowKey: WINDOW_KEY_AUTOSAVE_CONFIG,
    autosaveRuntimeWindowKey: WINDOW_KEY_AUTOSAVE_RUNTIME,
    refreshSourceTag: SOURCE_UI_TABS_REFRESH,
    normalizeReviewState,
    getRememberedReviewState,
    rememberReviewState,
    parseReviewStateChangedPayload,
  });

  const { findLoadedEntities } = createEntityIndex({
    hostAdapter,
    toPositiveInt,
  });

  const { getAllTabs, getSampleInfoForReactionId, getActiveTab } = createTabsProjection({
    hostAdapter,
    findLoadedEntities,
    toPaneKey,
    parseUpdatedAtMs,
  });

  const {
    lockTreeByReactionId,
    unlockTreeByReactionId,
    updateTabs,
  } = createLockProjection({
    hostAdapter,
    findLoadedEntities,
    getSampleInfoForReactionId,
    normalizeReviewState,
  });

  const { handleReviewStateChanged, handleReviewActionError } = createReviewHandlers({
    hostAdapter,
    lockTreeByReactionId,
    unlockTreeByReactionId,
  });

  const { saveTab } = createSaveExecutor({
    hostAdapter,
    toPaneKey,
    getAllTabs,
  });

  const { acknowledge: acknowledgeNotifications } = createNotificationAcknowledgement({
    fetchFn: window.fetch.bind(window),
    isStaleNotificationId: presence.isChemkitStaleNotificationId,
    getClientIdHeader: presence.chemkitClientIdHeader,
  });

  const {
    getActiveCollection,
    getCurrentUserId,
    readAutoSaveConfig,
    openReaction,
  } = hostAdapter;

  window.chemkit = composeBridge(window.chemkit, {
    getAllTabs,
    getActiveTab,
    saveTab,
    getActiveCollection,
    getCurrentUserId,
    acknowledgeNotifications,
    readAutoSaveConfig,
    openReaction,
    getSampleInfoForReactionId,
    lockTreeByReactionId,
    unlockTreeByReactionId,
    handleReviewStateChanged,
    handleReviewActionError,
    updateTabs,
  });

  // eslint-disable-next-line no-console
  console.log('[chemkit] bridge installed');

  window.dispatchEvent(new CustomEvent(EVENT_ADDON_WAKE));
}
